<template>
  <AlertConfirmModal ref="alertConfirmModal" />
  <v-dialog
    v-model="isVisible"
    max-height="75%"
    max-width="75%"
    persistent
    @after-leave="cleanup"
  >
    <v-card>
      <v-card-title>
        <template v-if="mode === 'import'">
          {{ langKey['titleImportDataModal'] }}
        </template>
        <template v-else>
          {{ langKey['titleExportDataModal'] }}
        </template>
      </v-card-title>
      <v-card-text class="scrollable-card-text">
        <template v-if="mode === 'import'">
          {{ langKey['labelChooseColumnsToImport'] }}
          <v-file-input
            accept=".csv"
            append-inner-icon="mdi-upload"
            class="mb-3"
            density="compact"
            variant="outlined"
            v-model="importFile"
            hide-details
          />
          <v-data-table
            :headers="tableHeaders"
            :items="tableRows"
            class="elevation-1"
          >
            <template #item="{ item }">
              <tr>
                <td v-for="header in tableHeaders" :key="header.key">
                  <span v-if="header.key === 'created' || header.key === 'updated'">
                    {{ new Date(Number(item[header.key])).toLocaleString() }}
                  </span>
                  <template v-else-if="header.key === 'actions'">
                    <div v-if="item.isEdit" class="d-flex ga-2">
                      <v-btn icon density="compact" @click="cancelEditCell(item)" flat>
                        <v-icon icon="mdi-cancel"></v-icon>
                      </v-btn>
                      <v-btn icon density="compact" @click="saveEditCell(item)" flat>
                        <v-icon icon="mdi-content-save-outline"></v-icon>
                      </v-btn>
                    </div>
                    <div v-else class="d-flex ga-2">
                      <v-btn density="compact" icon @click="editCell(item)" flat>
                        <v-icon icon="mdi-pencil-outline"></v-icon>
                      </v-btn>
                      <v-btn density="compact" icon @click="deleteCell(item)" flat>
                        <v-icon  icon="mdi-trash-can"></v-icon>
                      </v-btn>
                    </div>
                  </template>
                  <span v-else>
                    <v-textarea
                      v-if="item.isEdit"
                      class="resizable-textarea"
                      density="compact"
                      variant="outlined"
                      v-model.trim="item.editCache[header.key]"
                      rows="1"
                      hide-details
                    />
                    <span v-else>
                      {{ getCellValue(item, header.key) }}
                    </span>
                  </span>
                </td>
              </tr>
            </template>
          </v-data-table>
        </template>
        <template v-else>
          {{ langKey['labelChooseColumnsToExport'] }}
          <v-checkbox
            v-model="selectAll"
            :indeterminate="isIndeterminate"
            :label="langKey['labelSelectAll']"
            color="#586CDA"
            density="compact"
            hide-details
            @change="toggleSelectAll"
          />
          <div style="max-height: 300px; overflow-y: auto; border: 1px solid #ccc;">
            <template v-for="(col, colIdx) in columns" :key="colIdx">
              <v-checkbox
                :label="col.displayName"
                :value="col.name"
                color="#586CDA"
                density="compact"
                v-model="selectedColumns"
                hide-details
              >
              </v-checkbox>
            </template>
          </div>
          <div v-if="hasColumnError" style="color: red;">
            {{ langKey['labelChooseOneOfColumns'] }}
          </div>
          {{ langKey['labelExportAs'] }}
          <div class="d-flex ga-2">
            <v-text-field
              :placeholder="langKey['placeholderExportFilename']"
              :rules="[v => !!v || langKey['labelRequired']]"
              density="comfortable"
              variant="outlined"
              v-model.trim="exportFilename"
              hide-details="auto"
            >
            </v-text-field>
            <v-select
              :items="exportOptions"
              item-title="displayName"
              item-value="name"
              density="comfortable"
              style="height: 48px;"
              variant="outlined"
              v-model.trim="exportAs"
              hide-details="auto"
            >
            </v-select>
          </div>
        </template>
      </v-card-text>
      <v-card-actions>
        <v-btn variant="flat" color="grey" style="width: 125px;" @click="handleCancel">{{ langKey.buttonCancel }}</v-btn>
        <v-btn variant="flat" color="#586CDA" style="width: 125px;" @click="handleConfirm">{{ langKey.buttonOk }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { defineComponent, ref, watch, computed, getCurrentInstance } from 'vue'

export default defineComponent({
  name: 'ImportExportModal',
  setup() {
    const { appContext } = getCurrentInstance()
    const langKey = appContext.config.globalProperties.langKey
    const isVisible = ref(false)
    const mode = ref(null) // 'import' | 'export'
    const columns = ref([])
    const selectedColumns = ref([])
    const selectAll = ref(false)
    const importFile = ref(null)
    const tableHeaders = ref([])
    const tableRows = ref([])
    const exportAs = ref("csv")
    const exportFilename = ref("export")
    const exportOptions = ref([
      {
        name: "csv",
        displayName: "CSV"
      },
      {
        name: "json",
        displayName: "JSON"
      }
    ])
    const hasColumnError = ref(false)
    const alertConfirmModal = ref(null)
    let resolveFunc

    const handleConfirm = () => {
      // Only validate export columns in export mode
      if (mode.value === "export" && !exportFilename.value) return;
      if (mode.value === "export" && selectedColumns.value.length === 0) {
        hasColumnError.value = true;
        return;
      }

      hasColumnError.value = false;

      if (mode.value === "import") {
        tableRows.value.forEach(item => {
          if (item.isEdit && item.editCache) {
            item.value = { ...item.editCache };
          }
          item.isEdit = false;
          item.editCache = null;
        });

        const exportedData = tableRows.value.map(item => ({
          // key: item.key,
          key: `language.${item.value.langKey}`,
          value: { ...item.value }
        }));

        resolveFunc({ data: exportedData });
      } else if (mode.value === "export") {
        resolveFunc({
          columns: [...selectedColumns.value],
          exportAs: exportAs.value,
          exportFilename: exportFilename.value
        });
      }

      isVisible.value = false;
    };

    const handleCancel = () => {
      resolveFunc(false)
      isVisible.value = false
    }

    const cleanup = () => {
      isVisible.value = false
      mode.value = null
      columns.value = []
      selectedColumns.value = []
      selectAll.value = false
      importFile.value = null
      tableHeaders.value = []
      tableRows.value = []
      exportAs.value = "csv"
      exportFilename.value = "export"
      hasColumnError.value = false
    }

    const openModal = (options) => {
      mode.value = options.mode
      columns.value = options.columns || []
      isVisible.value = true

      return new Promise((resolve) => {
        resolveFunc = resolve
      })
    }

    // Select All toggler
    const toggleSelectAll = () => {
      if (selectAll.value) {
        selectedColumns.value = columns.value.map(c => c.name)
      } else {
        selectedColumns.value = []
      }
    }

    const isIndeterminate = computed(() => {
      return (
        selectedColumns.value.length > 0 &&
        selectedColumns.value.length < columns.value.length
      )
    })

    // Keep selectAll in sync when user manually un/checks items
    watch(selectedColumns, (newVal) => {
      if (newVal.length === columns.value.length && newVal.length > 0) {
        // all selected
        selectAll.value = true
      } else {
        // none or some selected
        selectAll.value = false
      }
    })

    const parseCsv = (csvText) => {
      const lines = csvText.trim().split(/\r?\n/);
      const headers = lines[0].split(",");

      return lines.slice(1).map(line => {
        const cols = line.split(",");

        // Build row object based on headers
        const row = {};
        headers.forEach((h, i) => {
          row[h] = cols[i] || "";
        });

        // Transform into desired JSON shape
        return {
          key: row.key, // keep for API
          value: {
            langKey: row.langKey,
            en: row.en,
            tw: row.tw,
            cn: row.cn,
            ja: row.ja,
          },
          created: row.createdTime || Date.now(),
          updated: row.updatedTime || Date.now(),
          isEdit: false,
          editCache: null
        };
      });
    }

    const validateCsvHeaders = (csvText) => {
      const headers = csvText.trim().split(/\r?\n/)[0].split(",");
      
      // Check that all required headers exist
      const requiredHeaders = ["key", "langKey", "en", "tw", "cn", "ja"];
      const missing = requiredHeaders.filter(h => !headers.includes(h));
      
      if (missing.length > 0) {
        return {
          valid: false,
          // message: `Missing required headers: ${missing.join(", ")}`
          message: langKey["notifInvalidCsv"]
        };
      }

      return { valid: true };
    }
    
    const getTableHeaders = (csvText) => {
      const headers = csvText.trim().split(/\r?\n/)[0].split(",");

      return headers
        .filter(h => h !== "key")
        .map(h => ({
          title: h,
          key: ["langKey","en","tw","cn","ja"].includes(h) ? h : 
              h === "createdTime" ? "created" : 
              h === "updatedTime" ? "updated" : h
        }));
    }
    
    const getCellValue = (item, key) => {
      // langKey and languages are under item.value
      if (["langKey", "en", "tw", "cn", "ja"].includes(key)) {
        return item.value?.[key] || ''
      }
      // created/updated or other fields
      return item[key] || ''
    }

    const editCell = (item) => {
      // handle edit
      item.isEdit = true
      item.editCache = { ...item.value }
    }

    const cancelEditCell = (item) => {
      item.isEdit = false
      item.editCache = null
    }

    const saveEditCell = (item) => {
      item.value = { ...item.editCache }
      item.editCache = null
      item.isEdit = false
    }

    const deleteCell = async(item) => {
      if (!alertConfirmModal.value) return
      const modalInstance = alertConfirmModal.value
      const modalMessage = langKey["confirmDataDelete"]
      const showCancelButton = true
      const confirmed = await modalInstance.openModal(modalMessage, showCancelButton)
      if (confirmed) {
        tableRows.value = tableRows.value.filter(row => row.key !== item.key)
      }
    }

    watch(importFile, async (newFile) => {
      if (!newFile) {
        tableRows.value = []
        tableHeaders.value = []
        return
      }

      const reader = new FileReader()
      reader.onload = async function(e) {
        const csvText = e.target.result

        const validation = validateCsvHeaders(csvText)
        if (!validation.valid) {
          // Notify user and prevent import
          const modalInstance = alertConfirmModal.value
          await modalInstance.openModal(validation.message, false)
          importFile.value = null
          tableRows.value = []
          tableHeaders.value = []
          return
        }
        
        tableRows.value = parseCsv(csvText)
        tableHeaders.value = getTableHeaders(csvText)
        tableHeaders.value.push({
          title: langKey['labelActions'],
          key: "actions",
          align: "center"
        })
      }
      reader.readAsText(newFile)
    })

    return {
      cleanup,
      openModal,
      alertConfirmModal,
      hasColumnError,
      isVisible,
      mode,
      columns,
      importFile,
      tableHeaders,
      tableRows,
      parseCsv,
      validateCsvHeaders,
      getTableHeaders,
      getCellValue,
      editCell,
      cancelEditCell,
      saveEditCell,
      deleteCell,
      exportAs,
      exportFilename,
      exportOptions,
      selectedColumns,
      selectAll,
      isIndeterminate,
      toggleSelectAll,
      handleConfirm,
      handleCancel
    }
  }
})
</script>

<style scoped>
.resizable-textarea {
  resize: horizontal;
  overflow: auto;
  min-width: 100px;
}

.scrollable-card-text {
  max-height: 75vh;
  overflow-y: auto;
}
</style>
